import type { SiteSettings } from '@ottd/api';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface DocumentMeta {
  title?: string;
  description?: string;
  /** Admin-managed SEO settings (`GET /api/site/settings`); absent while loading. */
  site?: SiteSettings;
}

/**
 * The admin enters the site title/description once, in English (the legacy
 * site rendered them verbatim for everyone). They replace the English i18n
 * defaults only - de/es/pl keep their translated copy rather than showing an
 * English title in a translated page. Keywords are language-neutral enough to
 * apply everywhere.
 */
const SITE_SETTINGS_LANGUAGE = 'en';

const OG_TYPE = 'website';

type MetaAttribute = 'name' | 'property';

/** Upserts `<meta {attribute}="{key}" content>`; a null content removes the tag. */
function setMetaTag(attribute: MetaAttribute, key: string, content: string | null): void {
  const existing = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (content === null) {
    existing?.remove();
    return;
  }

  const tag = existing ?? document.head.appendChild(document.createElement('meta'));
  tag.setAttribute(attribute, key);
  tag.setAttribute('content', content);
}

/**
 * Sets the document `<title>`, meta description/keywords and the `og:*` tags
 * per route - the port of the legacy `ViewHelper::getHeaderMeta`. This is the
 * client-rendered SEO baseline (see the SEO decision in docs/phase-7a.md):
 * crawlers that execute JS get per-route metadata; non-JS crawlers (Discord and
 * social previews) get the static fallback in index.html. A prerender/SSR step
 * is deliberately deferred, not silently dropped.
 *
 * The `<html lang>` attribute is kept in step here too, so assistive tech and
 * crawlers see the language the page is actually rendered in.
 */
export function useDocumentMeta({ title, description, site }: DocumentMeta): void {
  const { t, i18n } = useTranslation('common');
  const { pathname } = useLocation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const isSiteLanguage = language?.split('-')[0] === SITE_SETTINGS_LANGUAGE;
  const baseTitle = (isSiteLanguage && site?.title) || t('meta.baseTitle');
  const defaultDescription = (isSiteLanguage && site?.description) || t('meta.description');
  const keywords = site?.keywords ?? null;

  useEffect(() => {
    const fullTitle = title ? `${title} · ${baseTitle}` : baseTitle;
    const fullDescription = description ?? defaultDescription;

    document.title = fullTitle;
    setMetaTag('name', 'description', fullDescription);
    setMetaTag('name', 'keywords', keywords);
    setMetaTag('property', 'og:type', OG_TYPE);
    setMetaTag('property', 'og:url', `${window.location.origin}${pathname}`);
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', fullDescription);

    return () => {
      document.title = baseTitle;
    };
  }, [title, description, baseTitle, defaultDescription, keywords, pathname]);

  useEffect(() => {
    if (language) document.documentElement.lang = language;
  }, [language]);
}
